import { createContext, useContext, useMemo, useState, useEffect } from "react";
import { useCartState } from "@made-people/grease-cart";
import { useBffClient } from "./use-bff-client";
import { useFrontendContext } from "./use-frontend-context";
import { frontendProductToGreaseProduct } from "../services/bff/mappers/frontend-product-to-grease-product";
import {
  ProductWithVariants,
  ProductSimple,
} from "@made-people/grease-product";

type WarehouseStockMap = Record<string, Record<string, number>>;

function toWarehouseStock(
  additionalInventory: { warehouseId: string; quantity: number }[],
): Record<string, number> {
  const warehouseStock: Record<string, number> = {};
  for (const inv of additionalInventory) {
    warehouseStock[inv.warehouseId] = inv.quantity;
  }
  return warehouseStock;
}

export interface CartWarehouseStockContextValue {
  stockByVariant: WarehouseStockMap;
  isLoading: boolean;
  hasError: boolean;
}

export const CartWarehouseStockContext =
  createContext<CartWarehouseStockContextValue>({
    stockByVariant: {},
    isLoading: true,
    hasError: false,
  });

export function useCartWarehouseStock(): CartWarehouseStockContextValue {
  return useContext(CartWarehouseStockContext);
}

export function useCartWarehouseStockFetch(): CartWarehouseStockContextValue {
  const { cart } = useCartState();
  const bffClient = useBffClient();
  const frontendContext = useFrontendContext();
  const [stockByVariant, setStockByVariant] = useState<WarehouseStockMap>({});
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const productIds = useMemo(() => {
    const ids = new Set<string>();
    for (const item of cart?.items ?? []) {
      if (item.type === "product") {
        ids.add(item.product.base.productId);
      }
    }
    return Array.from(ids);
  }, [cart?.items]);

  useEffect(() => {
    if (productIds.length === 0) {
      setStockByVariant({});
      setHasError(false);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setHasError(false);

    let cancelled = false;

    bffClient
      .fetchFrontendProductsCached(productIds, frontendContext)
      .then((response) => {
        if (cancelled) return;

        if (!response?.frontendProducts) {
          setHasError(true);
          setIsLoading(false);
          return;
        }

        const result: WarehouseStockMap = {};
        for (const fp of response.frontendProducts) {
          const product = frontendProductToGreaseProduct(
            fp,
            frontendContext,
            undefined,
          );
          if (!product) continue;

          if (product.dataModel === "with_variants") {
            for (const variant of (product as ProductWithVariants).variants) {
              result[variant.variantId] = toWarehouseStock(
                variant.additionalInventory,
              );
            }
          } else if (product.dataModel === "simple") {
            // Simple/one-size products carry per-warehouse stock directly on the
            // product (keyed by itemId, which matches the cart line item id).
            const simple = product as ProductSimple;
            result[simple.itemId] = toWarehouseStock(
              simple.additionalInventory,
            );
          }
        }

        setStockByVariant(result);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Failed to fetch cart warehouse stock", error);
        if (cancelled) return;
        setHasError(true);
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [productIds.join(",")]);

  return { stockByVariant, isLoading, hasError };
}
